import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetEntityResolution,
  getGetEntityResolutionQueryKey,
  useStartEntityScan,
  usePreviewAuthorMerge,
  useMergeAuthors,
  useDecideEntityCandidate,
  useControlEntityJob,
  ApiError,
  type AuthorMergePreview,
  type EntityResolutionCandidate,
  type EntityResolutionJob,
} from "@workspace/api-client-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { useAuthEnabled } from "@/lib/authQuery";
import { describeError, humanize, invalidateAfterCuration } from "@/components/curation/curationUtils";

/* ---------- helpers ---------- */

type Rec = Record<string, unknown>;
const ACTIVE = new Set(["queued", "pending", "running", "processing"]);
const isActive = (j: EntityResolutionJob) => ACTIVE.has(j.state);
const asRec = (v: unknown): Rec | null => (v && typeof v === "object" && !Array.isArray(v) ? (v as Rec) : null);
const str = (v: unknown) => (v == null ? "" : typeof v === "string" ? v : typeof v === "number" || typeof v === "boolean" ? String(v) : JSON.stringify(v));
const nums = (v: unknown): number[] => (Array.isArray(v) ? v.filter((x): x is number => typeof x === "number") : []);
const fmt = (n: number | null | undefined) => (n == null ? "n/a" : n.toFixed(3));

const btn =
  "inline-flex items-center justify-center rounded-xl px-3.5 py-2 text-sm font-medium transition active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1 disabled:opacity-50 disabled:active:scale-100 disabled:cursor-not-allowed";
const btnPrimary = `${btn} bg-stone-900 text-white hover:bg-stone-700`;
const btnGhost = `${btn} border border-stone-200 bg-white text-stone-700 hover:bg-stone-50`;
const btnDanger = `${btn} bg-rose-700 text-white hover:bg-rose-800`;
const card = "rounded-3xl border border-stone-200 bg-white p-5 shadow-sm sm:p-6";
const eyebrow = "text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-700";
const label = "mb-1 block text-xs font-semibold uppercase tracking-wide text-stone-500";

function stateTone(s: string) {
  if (s === "merged" || s === "completed" || s === "succeeded") return "bg-emerald-50 text-emerald-800 border-emerald-200";
  if (s === "rejected" || s === "cancelled" || s === "canceled") return "bg-stone-100 text-stone-600 border-stone-200";
  if (s === "failed" || s === "error") return "bg-rose-50 text-rose-800 border-rose-200";
  if (ACTIVE.has(s)) return "bg-sky-50 text-sky-800 border-sky-200";
  return "bg-amber-50 text-amber-800 border-amber-200";
}
function Pill({ s, testId }: { s: string; testId?: string }) {
  return <span data-testid={testId} className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${stateTone(s)}`}>{humanize(s)}</span>;
}

/** Pull known signal shapes out of opaque evidence JSON without inventing values. */
function readSignals(ev: Rec) {
  const reasons: string[] = [];
  for (const k of ["reasons", "signals", "matchedSignals", "notes"]) {
    const v = ev[k];
    if (Array.isArray(v)) v.forEach((x) => reasons.push(str(x)));
    else if (asRec(v)) Object.entries(asRec(v)!).forEach(([a, b]) => reasons.push(`${humanize(a)}: ${str(b)}`));
    else if (typeof v === "string") reasons.push(v);
  }
  const centroid: string[] = [];
  for (const [k, v] of Object.entries(ev)) if (/centroid|fresh|stale/i.test(k)) centroid.push(`${humanize(k)}: ${str(v)}`);
  const thematicOnly = ev.thematicOnly === true || ev.thematic_only === true || reasons.some((r) => /thematic[\s_-]?only/i.test(r));
  return { reasons, centroid, thematicOnly };
}

function errStatus(e: unknown) {
  return e instanceof ApiError ? e.status : null;
}

/* ---------- page ---------- */

export function EntityResolutionPage() {
  const qc = useQueryClient();
  const authed = useAuthEnabled();
  const [tab, setTab] = useState<"pending" | "rejected" | "merged">("pending");
  const [kindFilter, setKindFilter] = useState<"all" | "author" | "quote">("all");
  const [view, setView] = useState<"identity" | "exploratory">("identity");
  const [page,setPage]=useState(0);
  const params={state:tab,kind:kindFilter==="all"?undefined:kindFilter,view,offset:page*50};
  const ws = useGetEntityResolution(params,{
    query: {
      queryKey: getGetEntityResolutionQueryKey(params),
      enabled: authed,
      refetchInterval: (q) => (q.state.data?.jobs.some(isActive) ? 4000 : false),
    },
  });

  // Invalidate app-wide caches once a merge job transitions to finished.
  const prevJobs = useRef<Map<number, string>>(new Map());
  useEffect(() => {
    const jobs = ws.data?.jobs;
    if (!jobs) return;
    let finishedMerge = false;
    for (const j of jobs) {
      const prev = prevJobs.current.get(j.id);
      if (prev && ACTIVE.has(prev) && !ACTIVE.has(j.state) && /merge/.test(j.kind)) finishedMerge = true;
    }
    prevJobs.current = new Map(jobs.map((j) => [j.id, j.state]));
    if (finishedMerge) invalidateAfterCuration(qc, null);
  }, [ws.data, qc]);

  const [pair, setPair] = useState<{ source: string; target: string } | null>(null);
  const [quoteCand, setQuoteCand] = useState<EntityResolutionCandidate | null>(null);
  const [rejectCand, setRejectCand] = useState<EntityResolutionCandidate | null>(null);

  const candidates = ws.data?.candidates ?? [];
  const fallbackCounts = useMemo(() => {
    const c = { pending: 0, rejected: 0, merged: 0 };
    for (const x of candidates) {
      if (x.state === "rejected") c.rejected++;
      else if (x.state === "merged") c.merged++;
      else c.pending++;
    }
    return c;
  }, [candidates]);
  const counts=ws.data?.counts??fallbackCounts;
  const visible = candidates.filter((c) => {
    const bucket = c.state === "rejected" ? "rejected" : c.state === "merged" ? "merged" : "pending";
    return bucket === tab && (kindFilter === "all" || c.kind === kindFilter);
  });

  return (
    <div className="space-y-6" data-testid="page-entity-resolution">
      <nav className="flex flex-wrap items-center gap-2" aria-label="Harvest sections">
        <Link href="/harvest" className="rounded-full bg-stone-100 px-4 py-1.5 text-sm font-medium text-stone-600 transition hover:bg-stone-200" data-testid="link-harvest-queue">Harvest Queue</Link>
        <Link href="/harvest/governance" className="rounded-full bg-stone-100 px-4 py-1.5 text-sm font-medium text-stone-600 transition hover:bg-stone-200" data-testid="link-governance">Governance</Link>
        <Link href="/harvest/resolution" aria-current="page" className="rounded-full bg-stone-900 px-4 py-1.5 text-sm font-medium text-white shadow-sm" data-testid="link-resolution">Entity resolution</Link>
      </nav>

      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className={eyebrow}>Ledger of likenesses</p>
          <h2 className="mt-1 font-serif text-3xl text-stone-900">Entity resolution</h2>
          <p className="mt-1 max-w-2xl text-sm text-stone-500">Find author spellings and quote copies that may be the same thing. Nothing merges without a human reading both sides.</p>
        </div>
        <button className={btnGhost} onClick={() => ws.refetch()} disabled={ws.isFetching} data-testid="button-refresh-workspace">
          {ws.isFetching ? "Refreshing" : "Refresh"}
        </button>
      </header>

      {ws.isLoading ? (
        <div className="grid gap-4 [&>*]:min-w-0 lg:grid-cols-[minmax(0,1fr)_22rem]">
          <div className="space-y-3">{[1, 2, 3, 4].map((i) => <div key={i} className="h-28 animate-pulse rounded-3xl bg-stone-100" />)}</div>
          <div className="h-80 animate-pulse rounded-3xl bg-stone-100" />
        </div>
      ) : ws.isError || !ws.data ? (
        <div className={card} role="alert">
          <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{describeError(ws.error)}</p>
          <button className={`${btnGhost} mt-4`} onClick={() => ws.refetch()} data-testid="button-retry-workspace">Retry</button>
        </div>
      ) : (
        <div className="grid gap-6 [&>*]:min-w-0 lg:grid-cols-[minmax(0,1fr)_22rem]">
          <div className="min-w-0 space-y-6">
            <ScanPanel calibration={ws.data.calibration} />

            <section className={card} aria-label="Candidates">
              <div className="mb-4 space-y-2">
                <label className="flex flex-wrap items-center gap-2 text-sm text-stone-600">
                  Author evidence
                  <select aria-label="Author evidence" value={view} onChange={e=>{setView(e.target.value as typeof view);setPage(0);}}
                    className="rounded-xl border border-stone-200 bg-white px-3 py-2">
                    <option value="identity">Identity-supported suggestions</option>
                    <option value="exploratory">Exploratory / legacy evidence</option>
                  </select>
                </label>
                <p className="text-xs text-stone-500">Default: strong name matches or corroborated identity leads, ranked by evidence strength. Theme-only, weak-name and old matcher evidence are separate; start a new author scan to reassess legacy pending pairs. Reviewed decisions are never reopened by a rescan. Quote suggestions are unchanged.</p>
              </div>
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div role="tablist" aria-label="Candidate state" className="inline-flex gap-1 rounded-2xl bg-stone-100 p-1">
                  {(["pending", "rejected", "merged"] as const).map((t) => (
                    <button key={t} role="tab" aria-selected={tab === t} onClick={() => {setTab(t);setPage(0);}} data-testid={`tab-${t}`}
                      className={`rounded-xl px-3 py-1.5 text-sm font-medium transition ${tab === t ? "bg-white text-stone-900 shadow-sm" : "text-stone-500 hover:text-stone-800"}`}>
                      {humanize(t)} <span className="ml-1 tabular-nums text-stone-400">{counts[t]}</span>
                    </button>
                  ))}
                </div>
                <select aria-label="Candidate kind" value={kindFilter} onChange={(e) => {setKindFilter(e.target.value as typeof kindFilter);setPage(0);}} data-testid="select-kind-filter"
                  className="rounded-xl border border-stone-200 bg-white px-2.5 py-2 text-sm text-stone-700 focus:outline-none focus:ring-2 focus:ring-stone-400">
                  <option value="all">All kinds</option>
                  <option value="author">Authors</option>
                  <option value="quote">Quotes</option>
                </select>
              </div>

              {visible.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-stone-200 p-10 text-center">
                  <p className="font-serif text-xl text-stone-700">{tab === "pending" ? "Nothing awaiting a decision." : `No ${tab} candidates.`}</p>
                  <p className="mt-1 text-sm text-stone-400">{tab === "pending" ? "Start a scan above to look for likely duplicates." : "Decisions will be recorded here."}</p>
                </div>
              ) : (
                <ul className="space-y-3">
                  {visible.map((c) => (
                    <CandidateRow key={c.id} c={c}
                      onAuthor={() => setPair({ source: c.source, target: c.target })}
                      onQuote={() => setQuoteCand(c)}
                      onReject={() => setRejectCand(c)} />
                  ))}
                </ul>
              )}
              {(ws.data.totalCandidates>50 || page>0) && <div className="mt-4 flex items-center justify-between gap-3 text-sm">
                <button className="rounded-xl border border-stone-200 px-3 py-2 disabled:opacity-40" disabled={page===0} onClick={()=>setPage(p=>p-1)}>Previous</button>
                <span>Page {page+1} · {ws.data.totalCandidates} candidates</span>
                <button className="rounded-xl border border-stone-200 px-3 py-2 disabled:opacity-40" disabled={(page+1)*50>=ws.data.totalCandidates} onClick={()=>setPage(p=>p+1)}>Next</button>
              </div>}
            </section>

            <ManualPair onOpen={(s, t) => setPair({ source: s, target: t })} />
          </div>

          <aside className="min-w-0 space-y-6">
            <JobsPanel jobs={ws.data.jobs} />
            <CalibrationPanel calibration={ws.data.calibration} />
          </aside>
        </div>
      )}

      <AuthorMergeDialog pair={pair} onClose={() => setPair(null)} />
      <QuoteMergeDialog cand={quoteCand} onClose={() => setQuoteCand(null)} />
      <RejectDialog cand={rejectCand} onClose={() => setRejectCand(null)} />
    </div>
  );
}

/* ---------- threshold control ---------- */

function ThresholdField({ id, value, onChange }: { id: string; value: number | null; onChange: (n: number | null) => void }) {
  return (
    <div>
      <label htmlFor={id} className={label}>Distance cutoff (admin-selected)</label>
      <div className="flex items-center gap-3">
        <input id={id} type="range" min={0} max={2} step={0.01} value={value ?? 0} onChange={(e) => onChange(Number(e.target.value))}
          className="h-2 flex-1 accent-stone-900" data-testid={`input-${id}`} aria-describedby={`${id}-hint`} />
        <input type="number" min={0} max={2} step={0.01} value={value ?? ""} placeholder="unset" aria-label="Distance cutoff value"
          onChange={(e) => { if (e.target.value === "") return onChange(null); const n = Number(e.target.value); if (Number.isFinite(n)) onChange(Math.min(2, Math.max(0, n))); }}
          className="w-20 rounded-lg border border-stone-200 px-2 py-1 text-right font-mono text-sm tabular-nums" data-testid={`input-${id}-number`} />
      </div>
      <p id={`${id}-hint`} className="mt-1 text-xs text-stone-500">Unvalidated. Lower is stricter (0 = identical, 2 = opposite). No calibrated default exists until human-reviewed examples accumulate.{value == null && <strong className="block text-amber-800">Not selected. Choose a value explicitly.</strong>}</p>
    </div>
  );
}

/* ---------- scans ---------- */

function ScanPanel({ calibration }: { calibration: Rec }) {
  const qc = useQueryClient();
  const [kind, setKind] = useState<"author_scan" | "quote_scan">("author_scan");
  const [threshold, setThreshold] = useState<number | null>(null);
  const [ack, setAck] = useState(false);
  useEffect(() => { setAck(false); }, [threshold, kind]);
  const [author, setAuthor] = useState("");
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const scan = useStartEntityScan();
  const hasExamples = Object.values(calibration).some((v) => { const r = asRec(v); return r && (nums(r.accepted).length + nums(r.rejected).length) > 0; });

  function start() {
    if (threshold == null || !ack) return;
    setMsg(null);
    scan.mutate(
      { data: kind === "author_scan" ? { kind, threshold } : { kind, threshold, author: author.trim() || null } },
      {
        onSuccess: (j) => { setAck(false); setMsg({ ok: true, text: `Scan job #${j.id} ${j.state}.` }); qc.invalidateQueries({ queryKey: getGetEntityResolutionQueryKey() }); },
        onError: (e) => setMsg({ ok: false, text: describeError(e) }),
      },
    );
  }

  return (
    <section className={card} aria-label="Start a scan">
      <p className="mb-3 text-xs text-stone-500">Scans compare Library-eligible quotes and authors with at least one such quote. Content opt-in settings do not change scan scope.</p>
      <p className="mb-3 text-xs text-stone-500">Author scans first examine high-signal name, alias and quote/work-supported leads across the catalog, then run a broader blocked-name and centroid pass. Both passes must finish for full coverage. Centroid distance measures themes, not personal identity.</p>
      <p className={eyebrow}>New sweep</p>
      <h3 className="mt-1 text-xl font-semibold text-stone-900">Scan for likely duplicates</h3>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <div role="radiogroup" aria-label="Scan kind" className="flex gap-2">
          {([["author_scan", "Authors"], ["quote_scan", "Quotes"]] as const).map(([v, l]) => (
            <button key={v} role="radio" aria-checked={kind === v} onClick={() => setKind(v)} data-testid={`radio-scan-${v}`}
              className={`flex-1 rounded-2xl border px-3 py-3 text-left text-sm transition ${kind === v ? "border-stone-900 bg-stone-900 text-white" : "border-stone-200 text-stone-700 hover:bg-stone-50"}`}>
              <span className="block font-semibold">{l}</span>
              <span className={`text-xs ${kind === v ? "text-stone-300" : "text-stone-500"}`}>{v === "author_scan" ? "Name variants, same person" : "Near-identical quote text"}</span>
            </button>
          ))}
        </div>
        <ThresholdField id="scan-threshold" value={threshold} onChange={setThreshold} />
        {kind === "quote_scan" && (
          <div className="md:col-span-2">
            <label htmlFor="scan-author" className={label}>Author (optional)</label>
            <input id="scan-author" value={author} maxLength={160} onChange={(e) => setAuthor(e.target.value)} placeholder="Leave empty to scan all Library authors"
              className="w-full rounded-xl border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-400" data-testid="input-scan-author" />
          </div>
        )}
      </div>
      {!hasExamples && <p className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">No human-reviewed examples yet. Treat this cutoff as an exploratory guess; review results generously.</p>}
      {threshold != null && (
        <label className="mt-3 flex cursor-pointer items-start gap-2 rounded-2xl border border-amber-200 bg-white p-3 text-sm text-amber-900">
          <input type="checkbox" className="mt-0.5 h-4 w-4 accent-amber-700" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="checkbox-ack-cutoff" />
          I chose cutoff {threshold.toFixed(2)} myself and understand it is uncalibrated.
        </label>
      )}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        {msg ? <p role={msg.ok ? "status" : "alert"} className={`text-sm ${msg.ok ? "text-emerald-700" : "text-rose-700"}`}>{msg.text}</p> : <span />}
        <button className={btnPrimary} onClick={start} disabled={scan.isPending || threshold == null || !ack} data-testid="button-start-scan">{scan.isPending ? "Starting" : "Start scan"}</button>
      </div>
    </section>
  );
}

/* ---------- candidates ---------- */

function CandidateRow({ c, onAuthor, onQuote, onReject }: { c: EntityResolutionCandidate; onAuthor: () => void; onQuote: () => void; onReject: () => void }) {
  const ev = asRec(c.evidence) ?? {};
  const { reasons, centroid, thematicOnly } = readSignals(ev);
  const pending = c.state !== "merged" && c.state !== "rejected";
  const srcQ = asRec(ev.source), tgtQ = asRec(ev.target);
  const decisionReason = str((c as EntityResolutionCandidate & { reason?: string | null }).reason ?? ev.decisionReason ?? "");
  return (
    <li className="rounded-2xl border border-stone-200 p-4 transition-colors hover:border-stone-300" data-testid={`card-candidate-${c.id}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs text-stone-500">
        <span className="rounded-md bg-stone-100 px-1.5 py-0.5 font-mono">#{c.id}</span>
        <span className="font-semibold uppercase tracking-wide">{c.kind}</span>
        <Pill s={c.state} testId={`status-candidate-${c.id}`} />
        <span className="ml-auto font-mono tabular-nums" title="Cosine distance; lower is closer">d = {fmt(c.distance)}</span>
      </div>
      {c.kind === "quote" && srcQ && tgtQ ? (
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {[srcQ, tgtQ].map((q, i) => (
            <blockquote key={i} className="rounded-xl bg-stone-50 p-3 font-serif text-[15px] leading-snug text-stone-800">
              &ldquo;{str(q.text)}&rdquo;
              <footer className="mt-1 font-sans text-xs text-stone-500">{str(q.author)} · #{str(q.id)} · {str(q.status)}</footer>
            </blockquote>
          ))}
        </div>
      ) : (
        <p className="mt-2 text-stone-900">
          <span className="font-serif text-lg">{c.source}</span>
          <span className="mx-2 text-stone-400" aria-hidden>vs</span>
          <span className="font-serif text-lg">{c.target}</span>
        </p>
      )}
      {(reasons.length > 0 || centroid.length > 0 || thematicOnly) && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {thematicOnly && <span className="rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-800">Thematic similarity only: not evidence of identity</span>}
          {centroid.map((s) => <span key={s} className="rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[11px] text-sky-800">{s}</span>)}
          {reasons.map((s, i) => <span key={i} className="rounded-full bg-stone-100 px-2 py-0.5 text-[11px] text-stone-600">{s}</span>)}
        </div>
      )}
      {c.kind === "author" && (
        <div className="mt-3 space-y-1 text-xs text-stone-600">
          <p>Evidence: {humanize(str(ev.identityTier) || "legacy — awaiting rescan")} · matcher {str(ev.matcherVersion) || "legacy"}. Not a duplicate probability.</p>
          {typeof ev.quoteOverlap === "number" && ev.quoteOverlap > 0 && <p>Shared exact Library quote texts: {ev.quoteOverlap}. Corroboration only; attribution may be wrong.</p>}
          {Array.isArray(ev.conflicts) && ev.conflicts.map((s,i)=><p key={i} className="text-amber-800">{str(s)}</p>)}
          {Array.isArray(ev.aliasEvidence) && ev.aliasEvidence.map((v,i)=>{
            const relation=asRec(v);
            return relation ? <blockquote key={i} className="border-l-2 border-amber-300 pl-2">Unverified biography alias: {str(relation.span)}</blockquote> : null;
          })}
          {Array.isArray(ev.supportingEvidence) && ev.supportingEvidence.map((v,i)=>{
            const item=asRec(v);
            return item ? <details key={i} className="border-l-2 border-stone-300 pl-2">
              <summary className="cursor-pointer">Supporting evidence (unverified): {humanize(str(item.kind))} · {str(item.value)}</summary>
              {Array.isArray(item.spans) && item.spans.map((span,j)=><blockquote key={j} className="mt-1 [overflow-wrap:anywhere]">{str(span)}</blockquote>)}
            </details> : null;
          })}
        </div>
      )}
      {!pending && decisionReason && <p className="mt-2 text-xs text-stone-500">Reason: {decisionReason}</p>}
      <details className="mt-2 text-xs text-stone-500">
        <summary className="cursor-pointer select-none [overflow-wrap:anywhere] hover:text-stone-800">Raw evidence · rev {c.sourceRevision} / {c.targetRevision}</summary>
        <pre className="mt-2 max-h-56 overflow-auto rounded-lg bg-stone-50 p-2 font-mono text-[11px] text-stone-700">{JSON.stringify(c.evidence, null, 2)}</pre>
      </details>
      {pending && (
        <div className="mt-3 flex flex-wrap justify-end gap-2">
          <button className={btnGhost} onClick={onReject} data-testid={`button-reject-${c.id}`}>Not the same</button>
          {c.kind === "author"
            ? <button className={btnPrimary} onClick={onAuthor} data-testid={`button-review-author-${c.id}`}>Review pair</button>
            : <button className={btnPrimary} onClick={onQuote} data-testid={`button-review-quote-${c.id}`}>Choose survivor</button>}
        </div>
      )}
    </li>
  );
}

function ManualPair({ onOpen }: { onOpen: (s: string, t: string) => void }) {
  const [s, setS] = useState(""); const [t, setT] = useState("");
  const ok = s.trim() && t.trim() && s.trim() !== t.trim();
  return (
    <section className={card} aria-label="Compare two authors">
      <h3 className="text-lg font-semibold text-stone-900">Compare any two authors</h3>
      <p className="text-sm text-stone-500">For known variants the scan missed. Opens the same preview.</p>
      <div className="mt-3 grid min-w-0 gap-2 sm:grid-cols-2">
        <input aria-label="Source author" value={s} maxLength={160} onChange={(e) => setS(e.target.value)} placeholder="Merge from" className="min-w-0 w-full rounded-xl border border-stone-200 px-3 py-2 text-sm" data-testid="input-manual-source" />
        <input aria-label="Target author" value={t} maxLength={160} onChange={(e) => setT(e.target.value)} placeholder="Into (survivor)" className="min-w-0 w-full rounded-xl border border-stone-200 px-3 py-2 text-sm" data-testid="input-manual-target" />
        <button className={`${btnPrimary} sm:col-span-2 sm:justify-self-end`} disabled={!ok} onClick={() => onOpen(s.trim(), t.trim())} data-testid="button-manual-preview">Preview</button>
      </div>
    </section>
  );
}

/* ---------- author merge ---------- */

function AuthorCard({ a, role }: { a: Rec; role: "absorbed" | "survivor" }) {
  const rows: [string, unknown][] = [["Profession", a.profession], ["Notability", a.notabilityTier], ["Quotes", a.quoteCount], ["Centroid", a.centroid], ["Revision", a.revision]];
  return (
    <div className={`rounded-2xl border p-4 ${role === "survivor" ? "border-emerald-300 bg-emerald-50/40" : "border-stone-200 bg-white"}`}>
      <p className={`text-[10px] font-semibold uppercase tracking-[0.16em] ${role === "survivor" ? "text-emerald-700" : "text-stone-500"}`}>{role === "survivor" ? "Survives" : "Folds in"}</p>
      <p className="mt-1 font-serif text-xl text-stone-900">{str(a.name)}</p>
      {str(a.bio) && <p className="mt-2 line-clamp-4 text-xs leading-relaxed text-stone-600">{str(a.bio)}</p>}
      <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
        {rows.map(([k, v]) => (<Fragment key={k}><dt className="text-stone-400">{k}</dt><dd className="truncate font-mono text-stone-700">{str(v) || "n/a"}</dd></Fragment>))}
      </dl>
    </div>
  );
}

function AuthorMergeDialog({ pair, onClose }: { pair: { source: string; target: string } | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [swapped, setSwapped] = useState(false);
  const [preview, setPreview] = useState<AuthorMergePreview | null>(null);
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [qt, setQt] = useState<number | null>(null);
  const [metaAck, setMetaAck] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const previewM = usePreviewAuthorMerge();
  const merge = useMergeAuthors();

  useEffect(() => { setSwapped(false); setPreview(null); setPreviewKey(null); setReason(""); setConfirm(false); setMetaAck(false); setQt(null); setError(null); setDone(null); }, [pair]);

  const source = pair ? (swapped ? pair.target : pair.source) : "";
  const target = pair ? (swapped ? pair.source : pair.target) : "";
  const key = `${source}\u0000${target}`;
  const fresh = preview && previewKey === key;
  // Direction edits invalidate any loaded preview.
  useEffect(() => { if (previewKey && previewKey !== key) { setPreview(null); setPreviewKey(null); setConfirm(false); setMetaAck(false); } }, [key, previewKey]);

  function runPreview() {
    setError(null);
    previewM.mutate({ data: { source, target } }, {
      onSuccess: (p) => { setPreview(p); setPreviewKey(key); setConfirm(false); setMetaAck(false); },
      onError: (e) => setError(describeError(e)),
    });
  }
  const isNotability = (c: string) => /^notability/i.test(c.trim());
  const notabilityConflicts = fresh ? preview!.conflicts.filter(isNotability) : [];
  const hardConflicts = fresh ? preview!.conflicts.filter((c) => !isNotability(c)) : [];
  const blocked = !!fresh && (hardConflicts.length > 0 || preview!.collisions.length > 0);
  const needsMetaAck = notabilityConflicts.length > 0;
  const reasonOk = reason.trim().length >= 3 && reason.trim().length <= 1000;

  function commit() {
    if (!fresh || blocked || qt == null || (needsMetaAck && !metaAck)) return;
    setError(null);
    merge.mutate({ data: { source, target, token: preview!.token, reason: reason.trim(), quoteThreshold: qt, ...(needsMetaAck && metaAck ? { metadataResolution: "preserve_survivor" as const } : {}) } }, {
      onSuccess: (j) => {
        setDone(`Merge job #${j.id} ${j.state}. Track it in the jobs panel.`);
        qc.invalidateQueries({ queryKey: getGetEntityResolutionQueryKey() });
        invalidateAfterCuration(qc, null);
      },
      onError: (e) => {
        if (errStatus(e) === 409) { setPreview(null); setPreviewKey(null); setConfirm(false); setMetaAck(false); setError(`${describeError(e)} The preview was stale and has been cleared; preview again.`); }
        else setError(describeError(e));
      },
    });
  }

  return (
    <Dialog open={!!pair} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92dvh] min-w-0 w-[calc(100vw-1.5rem)] max-w-3xl overflow-y-auto break-words rounded-3xl border-stone-200 bg-[#fbfaf7] p-5 [&>*]:min-w-0 sm:p-6" data-testid="dialog-author-merge">
        <p className={eyebrow}>Same-person merge</p>
        <DialogTitle className="font-serif text-2xl text-stone-900">{source} <span className="text-stone-400">into</span> {target}</DialogTitle>
        <DialogDescription className="text-sm text-stone-500">
          Re-credits quotes from one author name to another. Previously published quotes stay published under the survivor as the same identity; private quotes are not newly approved. Real evidence is kept. This never merges user accounts or changes who originally submitted a quote.
        </DialogDescription>

        {done ? (
          <div className="py-8 text-center" role="status">
            <p className="font-serif text-xl text-stone-900">Queued.</p>
            <p className="mt-1 text-sm text-stone-500">{done}</p>
            <button className={`${btnPrimary} mt-5`} onClick={onClose} data-testid="button-close-merge">Close</button>
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl bg-stone-100 p-2">
              <span className="px-2 text-xs text-stone-600">Survivor: <strong className="text-stone-900">{target}</strong></span>
              <button className={btnGhost} onClick={() => setSwapped((v) => !v)} aria-label="Swap survivor direction" data-testid="button-swap-direction">Swap direction</button>
            </div>

            {!fresh && (
              <button className={`${btnPrimary} w-full`} onClick={runPreview} disabled={previewM.isPending} data-testid="button-preview-author">
                {previewM.isPending ? "Loading preview" : "Load preview"}
              </button>
            )}
            {previewM.isPending && <div className="grid gap-3 sm:grid-cols-2"><div className="h-40 animate-pulse rounded-2xl bg-stone-100" /><div className="h-40 animate-pulse rounded-2xl bg-stone-100" /></div>}

            {fresh && preview && (
              <>
                <div className="grid gap-3 sm:grid-cols-2">
                  <AuthorCard a={asRec(preview.source) ?? {}} role="absorbed" />
                  <AuthorCard a={asRec(preview.target) ?? {}} role="survivor" />
                </div>
                {hardConflicts.length > 0 && (
                  <div className="rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900" role="alert">
                    <p className="font-semibold">Conflicts block this merge</p>
                    <ul className="mt-1 list-disc pl-5 text-xs">{hardConflicts.map((c) => <li key={c}>{c}</li>)}</ul>
                  </div>
                )}
                {notabilityConflicts.length > 0 && (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                    <p className="font-semibold">Notability metadata differs</p>
                    <ul className="mt-1 list-disc pl-5 text-xs">{notabilityConflicts.map((c) => <li key={c}>{c}</li>)}</ul>
                    <p className="mt-1 text-xs">The higher tier is not chosen automatically. Adjudicate explicitly below, or leave unchecked and the merge stays blocked.</p>
                    {!blocked && (
                      <label className="mt-2 flex cursor-pointer items-start gap-2 rounded-xl bg-white p-2.5 text-sm">
                        <input type="checkbox" className="mt-0.5 h-4 w-4 accent-amber-700" checked={metaAck} onChange={(e) => setMetaAck(e.target.checked)} data-testid="checkbox-preserve-survivor-metadata" />
                        Keep "{target}" notability and protected decisions as-is, and preserve "{source}" original source history.
                      </label>
                    )}
                  </div>
                )}
                {preview.collisions.length > 0 && (
                  <div className="rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900" role="alert">
                    <p className="font-semibold">{preview.collisions.length} quote collision{preview.collisions.length > 1 ? "s" : ""} need explicit review</p>
                    <p className="text-xs">Open each quote to correct it or resolve the duplicate in the quote editor, or run a quote scan and resolve each pair with a chosen survivor. Author merge will not silently fold quotes together.</p>
                    <ul className="mt-2 space-y-1.5">
                      {preview.collisions.map((c, i) => { const r = asRec(c) ?? {}; return (
                        <li key={i} className="rounded-lg bg-white/70 p-2 text-xs">
                          <span className="font-serif">&ldquo;{str(r.text)}&rdquo;</span>
                          <span className="mt-1 flex flex-wrap gap-2">
                            {(["sourceId", "targetId"] as const).map((k) => str(r[k]) && (
                              <a key={k} href={`${import.meta.env.BASE_URL}quotes/${encodeURIComponent(str(r[k]))}`} target="_blank" rel="noreferrer" data-testid={`link-collision-${k}-${i}`}
                                className="rounded-md border border-rose-200 bg-white px-1.5 py-0.5 font-mono text-[11px] text-rose-800 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400">
                                Open #{str(r[k])} ({k === "sourceId" ? "source" : "survivor"})
                              </a>
                            ))}
                          </span>
                        </li>
                      ); })}
                    </ul>
                  </div>
                )}
                {!blocked && (
                  <>
                    <ThresholdField id="merge-quote-threshold" value={qt} onChange={setQt} />
                    <div>
                      <label htmlFor="merge-reason" className={label}>Reason (required)</label>
                      <textarea id="merge-reason" rows={2} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Transliteration variant; same birth and death dates"
                        className="w-full rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-400" data-testid="input-merge-reason" />
                    </div>
                    <label className="flex cursor-pointer items-start gap-2 rounded-2xl border border-rose-200 bg-white p-3 text-sm text-rose-900">
                      <input type="checkbox" className="mt-0.5 h-4 w-4 accent-rose-700" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} data-testid="checkbox-confirm-author-merge" />
                      I confirm "{source}" and "{target}" are the same person, and "{target}" survives.
                    </label>
                  </>
                )}
              </>
            )}

            {error && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>}

            <div className="flex flex-col-reverse gap-2 border-t border-stone-200 pt-4 sm:flex-row sm:justify-end">
              <button className={btnGhost} onClick={onClose}>Cancel</button>
              {fresh && <button className={btnGhost} onClick={runPreview} disabled={previewM.isPending} data-testid="button-repreview-author">Preview again</button>}
              <button className={btnDanger} onClick={commit} disabled={!fresh || blocked || qt == null || (needsMetaAck && !metaAck) || !reasonOk || !confirm || merge.isPending} data-testid="button-confirm-author-merge"
                title={blocked ? "Resolve conflicts and collisions first" : undefined}>
                {merge.isPending ? "Merging" : "Merge authors"}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------- quote merge ---------- */

function QuoteEvidence({ q, selected, onSelect, side }: { q: Rec; selected: boolean; onSelect: () => void; side: string }) {
  const counts = asRec(q.counts);
  return (
    <button type="button" role="radio" aria-checked={selected} onClick={onSelect} data-testid={`radio-survivor-${side}`}
      className={`min-w-0 w-full break-words rounded-2xl border p-4 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 ${selected ? "border-emerald-400 bg-emerald-50/50 ring-1 ring-emerald-300" : "border-stone-200 bg-white hover:border-stone-300"}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
        <span className="font-mono text-stone-500">#{str(q.id)} · rev {str(q.curationRevision)}</span>
        <span className={`font-semibold ${selected ? "text-emerald-700" : "text-stone-400"}`}>{selected ? "Survivor" : "Select as survivor"}</span>
      </div>
      <p className="mt-2 whitespace-pre-wrap font-serif text-base leading-snug text-stone-900">&ldquo;{str(q.text)}&rdquo;</p>
      <p className="mt-2 text-xs text-stone-600">{str(q.author)}{str(q.work) && ` · ${str(q.work)}`}</p>
      <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
        <Pill s={str(q.status) || "unknown"} />
        {str(q.visibility) && <span className="rounded-full bg-stone-100 px-2 py-0.5 text-stone-600">{str(q.visibility)}</span>}
        {counts && Object.entries(counts).map(([k, v]) => <span key={k} className="rounded-full bg-stone-100 px-2 py-0.5 text-stone-600">{humanize(k)} {str(v)}</span>)}
      </div>
      {(str(q.modelName) || str(q.inputFingerprint)) && <p className="mt-2 truncate font-mono text-[10px] text-stone-400">{str(q.modelName)} {str(q.inputFingerprint)}</p>}
    </button>
  );
}

function QuoteMergeDialog({ cand, onClose }: { cand: EntityResolutionCandidate | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [survivor, setSurvivor] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const decide = useDecideEntityCandidate();
  useEffect(() => { setSurvivor(null); setReason(""); setConfirm(false); setError(null); }, [cand?.id]);
  useEffect(() => { setConfirm(false); }, [survivor]);
  const ev = asRec(cand?.evidence) ?? {};
  const src = asRec(ev.source), tgt = asRec(ev.target);
  const { thematicOnly } = readSignals(ev);
  const srcId = src ? str(src.id) : cand?.source ?? "";
  const tgtId = tgt ? str(tgt.id) : cand?.target ?? "";
  const loser = survivor === srcId ? tgtId : srcId;
  const ok = !!survivor && confirm && reason.trim().length >= 3 && reason.trim().length <= 1000;

  function commit() {
    if (!cand || !survivor) return;
    setError(null);
    decide.mutate({ id: cand.id, data: { decision: "merge", reason: reason.trim(), survivor } }, {
      onSuccess: (w) => { qc.setQueryData(getGetEntityResolutionQueryKey(), w); invalidateAfterCuration(qc, null); onClose(); },
      onError: (e) => { setError(describeError(e)); if (errStatus(e) === 409) qc.invalidateQueries({ queryKey: getGetEntityResolutionQueryKey() }); },
    });
  }

  return (
    <Dialog open={!!cand} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92dvh] min-w-0 w-[calc(100vw-1.5rem)] max-w-4xl overflow-y-auto break-words rounded-3xl border-stone-200 bg-[#fbfaf7] p-5 [&>*]:min-w-0 sm:p-6" data-testid="dialog-quote-merge">
        <p className={eyebrow}>Quote duplicate · candidate #{cand?.id}</p>
        <DialogTitle className="font-serif text-2xl text-stone-900">Which record survives?</DialogTitle>
        <DialogDescription className="text-sm text-stone-500">Read both in full. There is no default; pick the survivor explicitly.</DialogDescription>
        {thematicOnly && <p className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900">Flagged as thematic similarity only. These may be different quotes on the same subject; reject unless the wording matches.</p>}
        {!src || !tgt ? (
          <p className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">Full quote evidence is missing for this candidate. Rerun the scan before merging.</p>
        ) : (
          <div role="radiogroup" aria-label="Survivor" className="mt-4 grid gap-3 md:grid-cols-2">
            <QuoteEvidence q={src} side="source" selected={survivor === srcId} onSelect={() => setSurvivor(srcId)} />
            <QuoteEvidence q={tgt} side="target" selected={survivor === tgtId} onSelect={() => setSurvivor(tgtId)} />
          </div>
        )}
        <div className="mt-4">
          <label htmlFor="quote-merge-reason" className={label}>Reason (required)</label>
          <textarea id="quote-merge-reason" rows={2} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)}
            className="w-full rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-400" data-testid="input-quote-merge-reason" />
        </div>
        {survivor && (
          <label className="mt-3 flex cursor-pointer items-start gap-2 rounded-2xl border border-rose-200 bg-white p-3 text-sm text-rose-900">
            <input type="checkbox" className="mt-0.5 h-4 w-4 accent-rose-700" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} data-testid="checkbox-confirm-quote-merge" />
            Fold #{loser} into survivor #{survivor}.
          </label>
        )}
        {error && <p role="alert" className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>}
        <div className="mt-4 flex flex-col-reverse gap-2 border-t border-stone-200 pt-4 sm:flex-row sm:justify-end">
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnDanger} disabled={!ok || !src || !tgt || decide.isPending} onClick={commit} data-testid="button-confirm-quote-merge">{decide.isPending ? "Merging" : "Merge quotes"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- reject ---------- */

function RejectDialog({ cand, onClose }: { cand: EntityResolutionCandidate | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const decide = useDecideEntityCandidate();
  useEffect(() => { setReason(""); setError(null); }, [cand?.id]);
  const ok = reason.trim().length >= 3 && reason.trim().length <= 1000;
  function commit() {
    if (!cand) return;
    decide.mutate({ id: cand.id, data: { decision: "reject", reason: reason.trim() } }, {
      onSuccess: (w) => { qc.setQueryData(getGetEntityResolutionQueryKey(), w); onClose(); },
      onError: (e) => { setError(describeError(e)); if (errStatus(e) === 409) qc.invalidateQueries({ queryKey: getGetEntityResolutionQueryKey() }); },
    });
  }
  return (
    <Dialog open={!!cand} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-md rounded-3xl border-stone-200 bg-[#fbfaf7] p-5 sm:p-6" data-testid="dialog-reject">
        <DialogTitle className="font-serif text-xl text-stone-900">Mark as distinct</DialogTitle>
        <DialogDescription className="text-sm text-stone-500">{cand?.source} and {cand?.target} are not the same. Your reason becomes a calibration example.</DialogDescription>
        <label htmlFor="reject-reason" className={`${label} mt-3`}>Reason (required)</label>
        <textarea id="reject-reason" rows={3} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Different people who share a surname"
          className="w-full rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-400" data-testid="input-reject-reason" />
        {error && <p role="alert" className="mt-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>}
        <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} disabled={!ok || decide.isPending} onClick={commit} data-testid="button-confirm-reject">{decide.isPending ? "Saving" : "Reject candidate"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- jobs ---------- */

function JobRow({ j }: { j: EntityResolutionJob }) {
  const qc = useQueryClient();
  const control = useControlEntityJob();
  const [err, setErr] = useState<string | null>(null);
  const p = asRec(j.progress) ?? {};
  const done = typeof p.done === "number" ? p.done : typeof p.processed === "number" ? p.processed : null;
  const total = typeof p.total === "number" ? p.total : null;
  const authorPass=j.kind==="author_scan" && typeof p.pass==="number" && typeof p.phase==="string" && j.config.matcherVersion;
  const pct = done != null && total ? Math.min(100, Math.round(
    ((authorPass && p.pass===2 ? total : 0)+done)/(total*(authorPass?2:1))*100)) : null;
  const active = isActive(j);
  const cancelled = j.state === "cancelled" || j.state === "canceled";
  const canRetry = j.state === "failed" || (cancelled && j.kind !== "author_merge");
  const mergeMoving = j.kind === "author_merge" && j.state === "running";
  function act(action: "retry" | "cancel") {
    setErr(null);
    control.mutate({ id: j.id, data: { action } }, {
      onSuccess: () => qc.invalidateQueries({ queryKey: getGetEntityResolutionQueryKey() }),
      onError: (e) => setErr(describeError(e)),
    });
  }
  return (
    <li className="rounded-2xl border border-stone-200 p-3" data-testid={`card-job-${j.id}`}>
      <div className="flex items-center gap-2 text-xs">
        <span className="font-mono text-stone-500">#{j.id}</span>
        <span className="font-semibold text-stone-800">{humanize(j.kind)}</span>
        <span className="ml-auto"><Pill s={j.state} testId={`status-job-${j.id}`} /></span>
      </div>
      {active && (
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-stone-100" role="progressbar" aria-valuenow={pct ?? undefined} aria-valuemin={0} aria-valuemax={100} aria-label={`Job ${j.id} progress`}>
          <div className={`h-full rounded-full bg-sky-500 ${pct == null ? "w-1/3 animate-pulse" : "transition-all duration-500"}`} style={pct != null ? { width: `${pct}%` } : undefined} />
        </div>
      )}
      {!!authorPass && <p className="mt-1.5 text-xs text-stone-600">
        {humanize(str(p.phase))} pass · high-signal examined {str(p.highSignalExamined ?? 0)} / {str(total)} · broad examined {str(p.broadExamined ?? 0)} / {str(total)}.
        {" "}Author visits may count both passes; only “coverage complete” confirms both finished.
      </p>}
      {active && j.state==="queued" && !!p.dbRetryAt && <p className="mt-1.5 rounded-lg bg-amber-50 px-2 py-1 text-xs text-amber-900">
        Database interrupted the scan. Automatic retry {str(p.dbLastAttempt)} of 5 is scheduled for {str(p.dbRetryAt)}.
        {" "}Saved progress and review decisions are retained.
      </p>}
      {!!p.dbLastCode && <p className="mt-1 text-xs text-stone-500">
        Last database error: {str(p.dbLastCode)} · stage {humanize(str(p.dbLastStage))} · checkpoint {str(p.dbLastCursor) || "start"}.
      </p>}
      {Object.keys(p).length > 0 && <p className="mt-1.5 text-[11px] text-stone-500 break-words">{Object.entries(p).map(([k, v]) => `${humanize(k)} ${str(v)}`).join(" · ")}</p>}
      {Object.keys(j.config ?? {}).length > 0 && <p className="mt-0.5 truncate font-mono text-[10px] text-stone-400">{JSON.stringify(j.config)}</p>}
      {j.error && <p className="mt-1.5 rounded-lg bg-rose-50 px-2 py-1 text-xs text-rose-800">{j.error}</p>}
      {err && <p role="alert" className="mt-1.5 text-xs text-rose-700">{err}</p>}
      {(active || canRetry) && (
        <div className="mt-2 flex justify-end gap-2">
          {active && (
            <button className={`${btnGhost} px-2.5 py-1 text-xs`} disabled={control.isPending || mergeMoving} onClick={() => act("cancel")} data-testid={`button-cancel-job-${j.id}`}
              title={mergeMoving ? "Author merges cannot be cancelled once quotes start moving" : undefined}>Cancel</button>
          )}
          {canRetry && <button className={`${btnPrimary} px-2.5 py-1 text-xs`} disabled={control.isPending} onClick={() => act("retry")} data-testid={`button-retry-job-${j.id}`}>Retry</button>}
        </div>
      )}
      {cancelled && j.kind === "author_merge" && <p className="mt-1 text-[10px] text-stone-400">Cancelled author merges need a fresh preview to rerun.</p>}
      {mergeMoving && <p className="mt-1 text-[10px] text-stone-400">Cancel is unavailable once an author merge starts moving quotes.</p>}
    </li>
  );
}

function JobsPanel({ jobs }: { jobs: EntityResolutionJob[] }) {
  const anyActive = jobs.some(isActive);
  return (
    <section className={card} aria-label="Jobs">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-stone-900">Jobs</h3>
        {anyActive && <span className="flex items-center gap-1.5 text-xs text-sky-700"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-sky-500" />Polling every 4s</span>}
      </div>
      {jobs.length === 0 ? <p className="rounded-2xl border border-dashed border-stone-200 p-6 text-center text-sm text-stone-400">No jobs yet.</p>
        : <ul className="space-y-2">{jobs.map((j) => <JobRow key={j.id} j={j} />)}</ul>}
    </section>
  );
}

/* ---------- calibration ---------- */

function CalibrationPanel({ calibration }: { calibration: Rec }) {
  const kinds = Object.entries(calibration).map(([k, v]) => [k, asRec(v)] as const).filter(([, v]) => v);
  return (
    <section className={card} aria-label="Calibration">
      <h3 className="text-lg font-semibold text-stone-900">Calibration</h3>
      <p className="mt-0.5 text-xs text-stone-500">Distances of human-decided pairs. Use these to inform, not dictate, your cutoff.</p>
      {kinds.length === 0 ? <p className="mt-3 rounded-2xl border border-dashed border-stone-200 p-5 text-center text-sm text-stone-400">No reviewed examples yet. All cutoffs are unvalidated.</p> : (
        <div className="mt-3 space-y-4">
          {kinds.map(([k, v]) => {
            const acc = nums(v!.accepted), rej = nums(v!.rejected);
            const all = [...acc, ...rej]; const max = Math.max(2, ...all);
            return (
              <div key={k} data-testid={`calibration-${k}`}>
                <p className="text-xs font-semibold uppercase tracking-wide text-stone-600">{humanize(k)}</p>
                <p className="text-[11px] text-stone-500">{acc.length} accepted · {rej.length} rejected{acc.length ? ` · accepted max ${fmt(Math.max(...acc))}` : ""}{rej.length ? ` · rejected min ${fmt(Math.min(...rej))}` : ""}</p>
                <div className="relative mt-1.5 h-6 rounded-lg bg-stone-50" aria-hidden>
                  {acc.map((d, i) => <span key={`a${i}`} className="absolute top-1 h-2 w-0.5 bg-emerald-600" style={{ left: `${(d / max) * 100}%` }} />)}
                  {rej.map((d, i) => <span key={`r${i}`} className="absolute bottom-1 h-2 w-0.5 bg-rose-600" style={{ left: `${(d / max) * 100}%` }} />)}
                </div>
                {all.length === 0 && <p className="text-[11px] text-amber-700">No examples for this kind.</p>}
              </div>
            );
          })}
          <p className="flex gap-3 text-[10px] text-stone-500"><span><span className="mr-1 inline-block h-2 w-0.5 bg-emerald-600" />Accepted</span><span><span className="mr-1 inline-block h-2 w-0.5 bg-rose-600" />Rejected</span></p>
        </div>
      )}
    </section>
  );
}
